import React, { useEffect, useRef, useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { CrmEmptyState, CrmErrorState, CrmPopover } from '../ui';

const DISPLAY_VALUE_KEYS = [
  'name', 'label', 'title', 'display_name', 'displayName', 'legal_name', 'legalName',
  'code', 'number', 'email', 'slug', 'value',
];

const plainTextFromHtml = (value) => {
  const text = String(value ?? '');
  if (!/<[^>]+>/.test(text)) return text;
  if (typeof document !== 'undefined') {
    const node = document.createElement('div');
    node.innerHTML = text;
    return String(node.textContent || node.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return text.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim();
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (React.isValidElement(value)) return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return plainTextFromHtml(value);
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatCellValue(item))
      .filter((item) => typeof item === 'string' && item.trim());
    return parts.length ? parts.join(', ') : (value.length ? `${value.length} items` : '');
  }
  if (typeof value === 'object') {
    for (const key of DISPLAY_VALUE_KEYS) {
      if (value[key] !== null && value[key] !== undefined && value[key] !== '') {
        return formatCellValue(value[key]);
      }
    }
    const primitiveEntries = Object.entries(value)
      .filter(([, entryValue]) => ['string', 'number', 'boolean', 'bigint'].includes(typeof entryValue))
      .slice(0, 3)
      .map(([key, entryValue]) => `${key.replace(/_/g, ' ')}: ${formatCellValue(entryValue)}`);
    return primitiveEntries.length ? primitiveEntries.join(', ') : `${Object.keys(value).length} fields`;
  }
  return String(value);
};

const columnMinWidth = (column) => Math.max(72, Number(column?.minWidth || 72));

function SelectAllCheckbox({ checked, indeterminate, onChange }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      className="crm-data-table__checkbox"
      checked={checked}
      onChange={onChange}
      aria-label="Select all matching records"
    />
  );
}

function CrmColumnHeader({
  column,
  sort,
  onSort,
  onFilterColumn,
  onEditColumns,
  onHideColumn,
  pinned,
  onTogglePin,
  onResize,
}) {
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const sortDirection = sort?.columnId === column.id ? sort.direction : null;

  const runAction = (action) => {
    action?.();
    setMenuOpen(false);
  };

  const resizeBy = (delta) => {
    const minWidth = columnMinWidth(column);
    onResize?.(Math.max(minWidth, Number(column.width || minWidth) + delta));
  };

  const beginResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const minWidth = columnMinWidth(column);
    const startWidth = Number(column.width || minWidth);
    const previousCursor = document.body.style.cursor;
    const previousSelection = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent) => {
      const width = Math.max(minWidth, startWidth + moveEvent.clientX - startX);
      onResize?.(width);
    };
    const finishResize = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', finishResize);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelection;
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', finishResize);
  };

  return (
    <th
      scope="col"
      style={{ width: column.width, minWidth: columnMinWidth(column), maxWidth: column.width }}
      className={[pinned ? 'is-pinned' : '', column.sortable !== false ? 'is-sortable' : ''].filter(Boolean).join(' ')}
      aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}
    >
      <div className="crm-data-table__header-content">
        {column.sortable !== false ? (
          <button
            type="button"
            className="crm-data-table__header-title"
            onClick={() => onSort?.({ columnId: column.id, direction: sortDirection === 'asc' ? 'desc' : 'asc' })}
          >{column.label}</button>
        ) : <span className="crm-data-table__header-title crm-data-table__header-title--static">{column.label}</span>}
        {sortDirection === 'asc' ? <ArrowUpwardIcon className="crm-data-table__sort-indicator" /> : null}
        {sortDirection === 'desc' ? <ArrowDownwardIcon className="crm-data-table__sort-indicator" /> : null}
        <button
          ref={anchorRef}
          type="button"
          className="crm-data-table__column-menu-button"
          onClick={(event) => { event.stopPropagation(); setMenuOpen((previous) => !previous); }}
          aria-label={`${column.label} column options`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <MoreVertIcon />
        </button>
      </div>

      <CrmPopover
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorRef={anchorRef}
        align="end"
        className="crm-column-menu"
      >
        {column.sortable !== false ? (
          <>
            <button role="menuitem" type="button" onClick={() => runAction(() => onSort({ columnId: column.id, direction: 'asc' }))}>
              <ArrowUpwardIcon /> Sort ascending
            </button>
            <button role="menuitem" type="button" onClick={() => runAction(() => onSort({ columnId: column.id, direction: 'desc' }))}>
              <ArrowDownwardIcon /> Sort descending
            </button>
          </>
        ) : null}
        {column.filterable ? (
          <button role="menuitem" type="button" onClick={() => runAction(onFilterColumn)}>
            <FilterListIcon /> Filter by this column
          </button>
        ) : null}
        {column.pinnable ? (
          <button role="menuitem" type="button" onClick={() => runAction(onTogglePin)}>
            <PushPinOutlinedIcon /> {pinned ? 'Unfreeze column' : 'Freeze column'}
          </button>
        ) : null}
        <button role="menuitem" type="button" onClick={() => runAction(onEditColumns)}>
          <AddIcon /> Add column
        </button>
        {column.hideable !== false ? (
          <button role="menuitem" type="button" onClick={() => runAction(onHideColumn)}>
            <RemoveIcon /> Remove column
          </button>
        ) : null}
      </CrmPopover>
      <span
        className="crm-data-table__resize-handle"
        role="separator"
        aria-label={`Resize ${column.label} column`}
        aria-orientation="vertical"
        tabIndex={0}
        onPointerDown={beginResize}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            resizeBy(-10);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            resizeBy(10);
          }
        }}
      />
    </th>
  );
}

function CrmDataTable({
  columns,
  rows,
  getRowId,
  selectedIds,
  allSelected,
  onToggleSelected,
  onToggleSelectAll,
  sort,
  onSort,
  pinnedColumnId,
  onTogglePin,
  onHideColumn,
  onEditColumns,
  onFilterColumn,
  onRowClick,
  onColumnResize,
  loading = false,
  error,
  onRetry,
  emptyTitle = 'No records match the current filters.',
  emptyMessage = 'Try changing or clearing the filters.',
  onClearFilters,
}) {
  const partiallySelected = selectedIds.length > 0 && !allSelected;

  return (
    <div className="crm-data-table-wrap">
      <table className="crm-data-table">
        <colgroup>
          <col className="crm-data-table__select-col" />
          {columns.map((column) => (
            <col key={column.id} style={{ width: column.width, minWidth: columnMinWidth(column), maxWidth: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="crm-data-table__select-cell is-pinned" scope="col">
              <SelectAllCheckbox
                checked={allSelected}
                indeterminate={partiallySelected}
                onChange={onToggleSelectAll}
              />
            </th>
            {columns.map((column) => (
              <CrmColumnHeader
                key={column.id}
                column={column}
                sort={sort}
                onSort={onSort}
                onFilterColumn={() => onFilterColumn?.(column)}
                onEditColumns={onEditColumns}
                onHideColumn={() => onHideColumn(column.id)}
                pinned={pinnedColumnId === column.id}
                onTogglePin={() => onTogglePin(column.id)}
                onResize={(width) => onColumnResize?.(column.id, width)}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? Array.from({ length: 9 }, (_, rowIndex) => (
            <tr key={`skeleton-${rowIndex}`} className="crm-data-table__skeleton-row" aria-hidden="true">
              <td className="crm-data-table__select-cell is-pinned"><span /></td>
              {columns.map((column, columnIndex) => (
                <td key={column.id} className={pinnedColumnId === column.id ? 'is-pinned' : ''}>
                  <span style={{ width: `${Math.max(34, 78 - columnIndex * 6)}%` }} />
                </td>
              ))}
            </tr>
          )) : null}

          {!loading && !error ? rows.map((row) => {
            const rowId = getRowId(row);
            const selected = selectedIds.includes(rowId);

            return (
              <tr
                key={rowId}
                className={[selected ? 'is-selected' : '', onRowClick ? 'is-clickable' : ''].filter(Boolean).join(' ')}
                onClick={() => onRowClick?.(row)}
              >
                <td className="crm-data-table__select-cell is-pinned" onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="crm-data-table__checkbox"
                    checked={selected}
                    onChange={() => onToggleSelected(rowId)}
                    aria-label={`Select ${columns[0]?.accessor?.(row) || 'record'}`}
                  />
                </td>
                {columns.map((column) => (
                  <td
                    key={column.id}
                    style={{ width: column.width, minWidth: columnMinWidth(column), maxWidth: column.width }}
                    className={[
                      pinnedColumnId === column.id ? 'is-pinned' : '',
                      column.className || '',
                    ].filter(Boolean).join(' ')}
                    onClick={column.stopRowClick || column.id === 'row_actions'
                      ? (event) => event.stopPropagation()
                      : undefined}
                  >
                    <div className="crm-data-table__cell-content" title={column.getTitle?.(row)}>
                      {typeof column.renderCell === 'function'
                        ? column.renderCell(row)
                        : formatCellValue(column.accessor?.(row) ?? row?.[column.id] ?? '')}
                    </div>
                  </td>
                ))}
              </tr>
            );
          }) : null}
        </tbody>
      </table>

      {!loading && error ? (
        <div className="crm-data-table__overlay">
          <CrmErrorState
            title="Couldn't load records."
            message={error}
            onRetry={onRetry}
          />
        </div>
      ) : null}

      {!loading && !error && !rows.length ? (
        <div className="crm-data-table__overlay">
          <CrmEmptyState
            title={emptyTitle}
            message={emptyMessage}
            actionLabel={onClearFilters ? 'Clear filters' : undefined}
            onAction={onClearFilters}
          />
        </div>
      ) : null}
    </div>
  );
}

export default CrmDataTable;
